// src/services/purchaseReceiptService.js
// SOT: purchase_receipts (Material Receive SOT per Constitution)
// D-291 P3d-4: the GRN no longer writes financial_transactions - what we owe
// the vendor is created by the PURCHASE BILL (purchaseBillService.js).
// Constitution: ALL business logic through service layer
// Constitution: UI MUST NEVER write Firestore directly

import {
  collection, updateDoc,
  doc, serverTimestamp, getDocs,
  query, where, runTransaction
} from "firebase/firestore";
import { db } from "../firebase";
import {
  MOVEMENT_TYPES,
  resolveSummaryRef,
  applyStockMovementsInTransaction,
} from "./inventoryService";
import {
  poFingerprint,
  summarizeGRNItems,
  roundMoney,
} from "../utils/grnPlan";

// Firestore allows 500 writes per transaction. Each stock line costs 2
// (stock doc + ledger row) plus receipt + PO — keep a wide margin.
const MAX_GRN_STOCK_LINES = 200;

// ─────────────────────────────────────────────────────
// STORE GRN — ONE TRANSACTION (D-292, Stabilization P1)
// ─────────────────────────────────────────────────────
// WHY: the old flow credited stock line by line (each its own write), then
// wrote the receipt, then locked the PO, then wrote the vendor bill — and
// never re-checked the lock. Two tabs, a double click or a retry after a
// part-way failure gave double stock and a double vendor bill (Security
// Ledger MED-16), and every ledger row showed stock before / after 0 / 0.
//
// NOW, inside ONE transaction:
//   1. re-read the PO: same company, po_status "ordered", not grn_locked,
//      not a Direct Site Purchase, lines unchanged since the screen opened
//   2. every stock line: stock doc + ledger row (real before / after)
//   3. purchase_receipts doc
//   4. PO update + grn_locked
// D-291 P3d-4: goods and bill are two events. The GRN writes NO vendor
// payable any more; the receipt is saved with bill_status "awaiting_bill"
// (something was received) or "not_needed" (Short Close, nothing came).
// The vendor's bill is entered later (Awaiting Bills ->
// purchaseBillService.createPurchaseBill), which creates the payable.
// A second tab / retry is refused at step 1. Offline or any failure →
// nothing is written. MRF lines are notified AFTER the commit (unchanged,
// non-blocking — reworked in P2).
//
// Totals (grand total, over / cancelled qty, PO status) are worked out HERE
// from updatedItems — the screen's own figures are for display only.
export const completeStoreGRN = async ({
  purchase_order_id,
  company_id,
  po_fingerprint,
  updatedItems,
  stockLines,
  receipt_type,          // "full" | "short_close"
  short_close_reason = "",
  completed_by,
  completed_by_name,
  actualFreight = 0,     // reference only — never in any total
}) => {
  if (!company_id)        throw new Error("company_id required");
  if (!purchase_order_id) throw new Error("purchase_order_id required");
  if (!completed_by)      throw new Error("Your login could not be read. Please log in again.");
  if (receipt_type !== "full" && receipt_type !== "short_close") {
    throw new Error("Invalid receipt type");
  }
  const isShortClose = receipt_type === "short_close";
  const reason = String(short_close_reason || "").trim();
  if (isShortClose && !reason) {
    throw new Error("Short close reason is mandatory.");
  }
  if (!Array.isArray(updatedItems) || updatedItems.length === 0) {
    throw new Error("No items to receive");
  }
  if (!Array.isArray(stockLines)) {
    throw new Error("stockLines required");
  }
  if (stockLines.length > MAX_GRN_STOCK_LINES) {
    throw new Error(
      `This GRN has ${stockLines.length} stock lines — the limit is ${MAX_GRN_STOCK_LINES}. ` +
      `Please contact support.`
    );
  }
  for (const line of stockLines) {
    const qty  = Number(line?.qty);
    const rate = Number(line?.rate || 0);
    if (!String(line?.item_id || "").trim()) throw new Error("Stock line without item_id");
    if (!Number.isFinite(qty) || qty <= 0)   throw new Error(`Invalid qty for ${line.item_name || line.item_id}`);
    if (!Number.isFinite(rate) || rate < 0)  throw new Error(`Invalid rate for ${line.item_name || line.item_id}`);
  }

  const totals = summarizeGRNItems(updatedItems);
  if (!isShortClose && totals.cancelledQty > 0) {
    throw new Error("Some items are short — use Short Close.");
  }
  const poStatusAfter = isShortClose
    ? "short_closed"
    : totals.hasOver ? "over_received" : "completed";

  // Stock docs are resolved BEFORE the transaction (a client transaction
  // cannot run queries); each ref is then read and written inside it.
  const refByItem = new Map();
  for (const line of stockLines) {
    const id = String(line.item_id).trim();
    if (!refByItem.has(id)) {
      refByItem.set(id, await resolveSummaryRef(company_id, id));
    }
  }

  const poRef      = doc(db, "purchase_orders", purchase_order_id);
  const receiptRef = doc(collection(db, "purchase_receipts"));
  const freight    = Number(actualFreight || 0) > 0 ? Number(actualFreight) : 0;
  // D-291 P3d-4: something received -> the vendor's bill is still to come.
  const billStatus = totals.grandTotal > 0 ? "awaiting_bill" : "not_needed";

  const receiptItems = updatedItems.map(item => ({
    ...item,
    amount: roundMoney(Number(item.received_qty || 0) * Number(item.rate || 0)),
  }));

  await runTransaction(db, async (transaction) => {
    // ── 1. Re-check the PO inside the transaction ──
    const poSnap = await transaction.get(poRef);
    if (!poSnap.exists()) {
      throw new Error("Purchase order not found");
    }
    const po = poSnap.data();
    if (po.company_id !== company_id) {
      throw new Error("Access denied — purchase order belongs to a different company");
    }
    if (po.grn_locked || po.po_status !== "ordered") {
      throw new Error(
        "This PO has already been received (or is no longer Ordered) — nothing was saved. " +
        "Please reload the page."
      );
    }
    if (po.purchase_mode === "direct_site_purchase") {
      throw new Error(
        "This is a Direct Site Purchase — it is received at site (Confirm → Finalize), not in the store."
      );
    }
    if (poFingerprint(po.items || []) !== po_fingerprint) {
      throw new Error(
        "This PO was changed after you opened the GRN page — nothing was saved. " +
        "Please reload and check the quantities again."
      );
    }

    // ── 2. Stock + ledger rows (reads stock docs, then writes) ──
    if (stockLines.length > 0) {
      await applyStockMovementsInTransaction(transaction, {
        company_id,
        movements: stockLines.map(line => ({
          summaryRef: refByItem.get(String(line.item_id).trim()),
          item: {
            item_id:   String(line.item_id).trim(),
            item_code: line.item_code || "",
            item_name: line.item_name || "",
            category:  line.category  || "",
            unit:      line.unit      || "",
          },
          stock_change: Number(line.qty),
          last_rate:    Number(line.rate) > 0 ? Number(line.rate) : null,
          ledger: {
            movement_type:           MOVEMENT_TYPES.GRN_IN,
            qty:                     Number(line.qty),
            reference_id:            purchase_order_id,
            reference_type:          line.reference_type || "purchase_grn",
            reconciliation_group_id: receiptRef.id,
            remarks:                 line.remarks || "",
            received_by:             completed_by,
            received_by_name:        completed_by_name || "",
          },
        })),
      });
    }

    // ── 3. Receipt ──
    transaction.set(receiptRef, {
      purchase_order_id,
      company_id,
      po_number:             po.po_number   || "",
      vendor_id:             po.vendor_id   || "",
      vendor_name:           po.vendor_name || "",
      receipt_type,
      items:                 receiptItems,
      grand_total:           totals.grandTotal,
      actual_freight_amount: freight,
      over_received:         totals.hasOver,
      over_qty:              totals.overQty,
      short_close:           isShortClose,
      short_close_reason:    isShortClose ? reason : "",
      total_cancelled_qty:   totals.cancelledQty,
      bill_status:           billStatus,
      completed_by,
      completed_by_name:     completed_by_name || "",
      created_at:            serverTimestamp(),
    });

    // ── 4. PO update + lock ──
    transaction.update(poRef, {
      items:                 updatedItems,
      po_status:             poStatusAfter,
      grn_locked:            true,
      // Same summary fields Direct Site finalize writes (were left
      // "pending" / 0 by the store GRN before D-292).
      receive_status:        "completed",
      received_items:        totals.receivedLines,
      pending_items:         0,
      total_items:           updatedItems.filter(i => !i._cancelled).length,
      ...(freight > 0 ? { actual_freight_amount: freight } : {}),
      short_closed:          isShortClose,
      short_close_reason:    isShortClose ? reason : "",
      short_closed_by:       isShortClose ? completed_by              : "",
      short_closed_by_name:  isShortClose ? (completed_by_name || "") : "",
      short_closed_at:       isShortClose ? serverTimestamp()         : null,
      over_received:         totals.hasOver,
      over_qty:              totals.overQty,
      total_cancelled_qty:   totals.cancelledQty,
      grand_total:           totals.grandTotal,
      purchase_receipt_id:   receiptRef.id,
      grn_completed_by:      completed_by,
      grn_completed_by_name: completed_by_name || "",
      grn_completed_at:      serverTimestamp(),
      updated_at:            serverTimestamp(),
      updated_by:            completed_by,
    });
    // (D-291 P3d-4: no vendor bill here - it comes from the purchase bill.)
  });

  // ── After the commit: MRF lines → ready to issue (non-blocking) ──
  await notifyMRFsAfterReceipt(
    updatedItems
      .filter(i => Number(i.received_qty || 0) > 0)
      .map(i => String(i.item_id || "").trim())
      .filter(Boolean),
    company_id
  );

  return {
    success:             true,
    receipt_id:          receiptRef.id,
    po_status:           poStatusAfter,
    grand_total:         totals.grandTotal,
    has_over:            totals.hasOver,
    total_cancelled_qty: totals.cancelledQty,
    bill_status:         billStatus,
  };
};

// ─────────────────────────────────────────────────────
// MRF NOTIFY — procurement_pending → approved_pending_issue
// ─────────────────────────────────────────────────────
const notifyMRFsAfterReceipt = async (receivedItemIds, companyId) => {
  try {
    const mrfSnap = await getDocs(query(
      collection(db, "material_requests"),
      where("company_id", "==", companyId),
      where("status", "in", ["approved", "partially_issued"])
    ));

    for (const mrfDoc of mrfSnap.docs) {
      const mrfData  = mrfDoc.data();
      const mrfItems = Array.isArray(mrfData.items) ? [...mrfData.items] : [];
      let   updated  = false;

      const newItems = mrfItems.map(item => {
        if (item.item_status !== "procurement_pending") return item;
        const itemId = String(item.item_id || "").trim();
        if (!itemId || !receivedItemIds.includes(itemId)) return item;
        updated = true;
        return {
          ...item,
          item_status:               "approved_pending_issue",
          stock_available_after_grn: true,
        };
      });

      if (updated) {
        await updateDoc(doc(db, "material_requests", mrfDoc.id), {
          items:      newItems,
          updated_at: serverTimestamp(),
        });
      }
    }
  } catch (err) {
    console.error("⚠ MRF notify failed (non-blocking):", err);
  }
};